import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import { useInView } from "react-intersection-observer";
import "./App.css";

// Components
import ProjectDetail from "./components/ProjectDetail";
import GitHubActivity from "./components/GitHubActivity";

// Icons
import {
  FiGithub,
  FiExternalLink,
  FiMail,
  FiLinkedin,
  FiTwitter,
  FiArrowDown,
  FiCode,
  FiUsers,
  FiDatabase,
  FiLayers,
  FiSettings,
  FiTrendingUp,
  FiMenu,
  FiX
} from "react-icons/fi";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const slideUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

function Portfolio() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "projects", "skills", "about", "contact"];
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="App bg-neutral-light min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg shadow-soft">
        <div className="container flex justify-between items-center py-4">
          <a href="#home" className="font-heading text-2xl font-bold gradient-text">aivApps</a>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden flex items-center text-neutral-dark"
            onClick={toggleMenu}
          >
            {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {["home", "projects", "skills", "about", "contact"].map((item) => (
              <a
                key={item}
                href={`#${item}`}
                className={`font-medium hover:text-primary transition-colors ${activeSection === item ? "text-primary" : "text-neutral-medium"
                  }`}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </a>
            ))}
          </nav>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <div className="container py-4 flex flex-col space-y-4">
              {["home", "projects", "skills", "about", "contact"].map((item) => (
                <a
                  key={item}
                  href={`#${item}`}
                  className={`font-medium hover:text-primary transition-colors ${activeSection === item ? "text-primary" : "text-neutral-medium"
                    }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.charAt(0).toUpperCase() + item.slice(1)}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </header>

      <main>
        {/* Hero Section */}
        <section id="home" className="hero-gradient min-h-screen flex items-center pt-16 pb-20">
          <div className="container">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="flex flex-col space-y-6"
              >
                <motion.div variants={fadeIn}>
                  <span className="text-lg md:text-xl text-primary font-medium">Hello, I'm a</span>
                </motion.div>

                <motion.h1 variants={slideUp} className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  <span className="gradient-text">Developer</span> Specializing in
                  <div className="mt-2">
                    <TypeAnimation
                      sequence={[
                        'APIs',
                        2000,
                        'Websites',
                        2000,
                        'Web Applications',
                        2000,
                        'Mobile Applications',
                        2000,
                        'Wordpress',
                        2000,
                      ]}
                      wrapper="span"
                      speed={30}
                      repeat={Infinity}
                    />
                  </div>
                </motion.h1>

                <motion.p variants={slideUp} className="text-lg md:text-xl text-neutral-medium max-w-xl">
                  I create elegant solutions through code, building powerful APIs
                  and stunning websites that solve real-world problems.
                </motion.p>

                <motion.div variants={fadeIn} className="flex flex-wrap gap-4 pt-4">
                  <a href="#projects" className="btn-primary">
                    View My Work <FiArrowDown className="ml-2" />
                  </a>
                  <a href="#contact" className="btn-outline">
                    Get In Touch
                  </a>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="hidden md:block"
              >
                <div className="relative">
                  <div className="absolute -top-6 -left-6 w-24 h-24 bg-primary/10 rounded-full blur-2xl"></div>
                  <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-secondary/10 rounded-full blur-2xl"></div>

                  <div className="rounded-2xl overflow-hidden border-8 border-white shadow-xl relative z-10">
                    <img
                      src="/assets/main.JPG"
                      alt="Developer at work"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <ProjectsSection />

        {/* Skills Section */}
        <SkillsSection />

        {/* About Section */}
        <AboutSection />

        {/* GitHub Activity Section */}
        <section className="section-padding bg-neutral-light">
          <div className="container">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <motion.h2
                className="font-bold mb-6 gradient-text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                GitHub Activity
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                Stay updated with my latest development work and contributions.
                Here are my most recent commits across various projects.
              </motion.p>
            </div>
            <div className="max-w-4xl mx-auto">
              <GitHubActivity />
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <ContactSection />
      </main>

      {/* Footer */}
      <footer className="footer-gradient py-12">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <p className="font-heading text-2xl font-bold gradient-text mb-2">aivApps</p>
              <p className="text-neutral-medium text-sm">
                © {new Date().getFullYear()} • All rights reserved
              </p>
            </div>

            <div className="flex space-x-6">
              <a href="https://github.com/GenaAiv" className="text-neutral-medium hover:text-primary transition-colors">
                <FiGithub size={20} />
              </a>
              <a href="https://www.linkedin.com/in/gena-aivazian-813095173/" className="text-neutral-medium hover:text-primary transition-colors">
                <FiLinkedin size={20} />
              </a>
              <a href="https://www.instagram.com/gennaaiv/" className="text-neutral-medium hover:text-primary transition-colors">
                <FiTwitter size={20} />
              </a>
              <a href="mailto:hello@example.com" className="text-neutral-medium hover:text-primary transition-colors">
                <FiMail size={20} />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Projects Section Component
function ProjectsSection() {
  const projects = [
    {
      id: 1,
      title: "Boxing RingBell Timer (iOS)",
      description: "A specialized iOS timer application for boxing training sessions, allowing users to set custom round timers, rest intervals, and manage training rounds with audio alerts.",
      image: "/assets/BoxingIcon.png",
      tags: ["Swift", "iOS", "Timer", "Audio", "User Interface"],
      github: "#",
      liveLink: "#",
    },
    {
      id: 2,
      title: "Plant Identification App (iOS)",
      description: "A free, ad-supported iOS app built with SwiftUI that identifies 400,000+ plant species using AI. Features unlimited free scans, comprehensive care guides, smart reminders, and personal plant collection management.",
      image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b",
      tags: ["Swift", "SwiftUI", "iOS", "AI/ML"],
      github: "#",
      liveLink: "#",
    },
    {
      id: 3,
      title: "Financial Dashboard",
      description: "A comprehensive financial dashboard for showcasing company earnings by products, featuring interactive charts, revenue analytics, and performance metrics visualization.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
      tags: ["React", "Chart.js", "D3.js", "Financial APIs", "Data Visualization"],
      github: "#",
      liveLink: "#",
    },
    {
      id: 4,
      title: "WordPress Websites",
      description: "Custom WordPress websites with responsive designs, e-commerce integration, and content management systems tailored for various business needs.",
      image: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d",
      tags: ["WordPress", "PHP", "MySQL", "Custom Themes", "WooCommerce"],
      github: "#",
      liveLink: "#",
    },
    {
      id: 5,
      title: "Three.js 3D Viewer",
      description: "An interactive 3D model viewer built with Three.js, featuring object manipulation, lighting controls, and material customization for immersive 3D experiences.",
      image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe",
      tags: ["Three.js", "WebGL", "JavaScript", "3D Graphics", "Interactive"],
      github: "#",
      liveLink: "#",
    },
  ];

  // Using the useInView hook for animation triggers
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="projects" className="section-padding bg-dots">
      <div className="container">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <motion.h2
            className="font-bold mb-6 gradient-text"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Featured Projects
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Explore my recent work showcasing API integrations, interactive web applications,
            and responsive website designs. Each project demonstrates my approach to solving complex problems.
          </motion.p>
        </div>

        <motion.div
          ref={ref}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// Project Card Component
function ProjectCard({ project }) {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/project/${project.id}`);
  };

  return (
    <motion.div
      variants={slideUp}
      className="project-card glass-card overflow-hidden relative group cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center rounded-t-2xl">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105 p-4 rounded-2xl"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-dark/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
          <div className="p-4 flex space-x-4">

          </div>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-bold mb-2">{project.title}</h3>
        <p className="text-neutral-medium mb-4 text-sm">{project.description}</p>
        <div className="flex flex-wrap gap-2">
          {project.tags.map((tag, index) => (
            <span
              key={index}
              className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-4 text-primary text-sm font-medium">
          Click to view details →
        </div>
      </div>
    </motion.div>
  );
}

// Skills Section Component
function SkillsSection() {
  const skillCategories = [
    {
      id: "frontend",
      title: "Frontend Development",
      icon: <FiLayers />,
      skills: ["React", "Vue.js", "Next.js", "Tailwind CSS", "JavaScript", "TypeScript", "HTML5/CSS3", "Responsive Design"]
    },
    {
      id: "backend",
      title: "Backend Development",
      icon: <FiDatabase />,
      skills: ["Node.js", "Express", "Python", "Flask", "MongoDB", "PostgreSQL", "REST APIs", "GraphQL"]
    },
    {
      id: "tools",
      title: "Tools & Other",
      icon: <FiSettings />,
      skills: ["Git", "Docker", "CI/CD", "AWS", "Firebase", "Figma", "Testing", "Performance Optimization"]
    }
  ];

  // Using the useInView hook for animation triggers
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section
      id="skills"
      className="section-padding relative"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1524169220946-12efd782aab4')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-white/90 backdrop-blur-sm"></div>

      <div className="container relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <motion.h2
            className="font-bold mb-6 gradient-text"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Skills & Expertise
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            With experience across the full development stack, I build complete digital solutions
            from concept to deployment. Here are the technologies and skills I work with.
          </motion.p>
        </div>

        <motion.div
          ref={ref}
          className="grid md:grid-cols-3 gap-8"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          {skillCategories.map((category) => (
            <SkillCategory key={category.id} category={category} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// Skill Category Component
function SkillCategory({ category }) {
  return (
    <motion.div
      variants={slideUp}
      className="glass-card p-6"
    >
      <div className="flex items-center mb-6">
        <div className="p-3 bg-primary/10 text-primary rounded-xl mr-4">
          {category.icon}
        </div>
        <h3 className="text-xl font-bold">{category.title}</h3>
      </div>

      <div className="flex flex-wrap gap-2">
        {category.skills.map((skill, index) => (
          <span
            key={index}
            className="skill-badge px-4 py-2 bg-white shadow-soft rounded-xl text-neutral-medium text-sm"
          >
            {skill}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

// About Section Component
function AboutSection() {
  // Using the useInView hook for animation triggers
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="about" className="section-padding bg-grid">
      <div className="container">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-secondary/10 rounded-full blur-xl"></div>
            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-primary/10 rounded-full blur-xl"></div>

            <div className="rounded-2xl overflow-hidden border-8 border-white shadow-xl relative z-10">
              <img
                src="https://images.unsplash.com/photo-1488590528505-98d2b5aba04b"
                alt="Developer coding"
                className="w-full h-auto object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="flex flex-col space-y-6"
          >
            <motion.h2 variants={slideUp} className="font-bold gradient-text">
              About Me
            </motion.h2>

            <motion.p variants={slideUp} className="text-neutral-medium">
              I'm Gena Aivazian, a passionate software developer based in Rotterdam.
              My coding journey began in mid-2019, and since then I've been dedicated to
              creating elegant solutions through web development, specializing in both
              frontend and backend technologies.
            </motion.p>

            <motion.p variants={slideUp} className="text-neutral-medium">
              As a problem solver at heart, I enjoy tackling complex challenges and turning
              ideas into functional, user-friendly applications. My experience spans from
              building weather applications and to-do apps to developing comprehensive
              portfolio websites and bot management systems using JavaScript, PHP, and TypeScript.
            </motion.p>

            <motion.div variants={slideUp}>
              <h3 className="text-lg font-semibold mb-3">My approach involves:</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="p-1 bg-primary/10 text-primary rounded-full mr-3 mt-1">
                    <FiCode size={14} />
                  </span>
                  <span>Writing clean, maintainable code that scales</span>
                </li>
                <li className="flex items-start">
                  <span className="p-1 bg-primary/10 text-primary rounded-full mr-3 mt-1">
                    <FiUsers size={14} />
                  </span>
                  <span>Focusing on user-centered design principles</span>
                </li>
                <li className="flex items-start">
                  <span className="p-1 bg-primary/10 text-primary rounded-full mr-3 mt-1">
                    <FiTrendingUp size={14} />
                  </span>
                  <span>Continuously learning and adapting to new technologies</span>
                </li>
              </ul>
            </motion.div>

            <motion.div variants={fadeIn} className="pt-4">
              <a href="#contact" className="btn-primary">
                Let's Work Together
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Contact Section Component
function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Using the useInView hook for animation triggers
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Submit to Netlify Forms
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        "form-name": "contact",
        "bot-field": "",
        ...formData
      }).toString()
    })
      .then(() => {
        setIsSubmitting(false);
        setSubmitStatus("success");
        setFormData({ name: "", email: "", message: "" });

        // Reset status after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      })
      .catch((error) => {
        console.error("Form submission error:", error);
        setIsSubmitting(false);
        setSubmitStatus("error");

        // Reset status after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      });
  };

  return (
    <section id="contact" className="section-padding hero-gradient">
      <div className="container">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <motion.h2
            className="font-bold mb-6 gradient-text"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Get In Touch
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Have a project in mind or want to discuss potential opportunities?
            I'd love to hear from you. Fill out the form below and I'll get back to you as soon as possible.
          </motion.p>
        </div>

        <motion.div
          ref={ref}
          className="max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <div className="glass-card p-8">
            {submitStatus === "success" ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
                <p className="text-neutral-medium">Thank you for reaching out. I'll get back to you shortly.</p>
              </div>
            ) : submitStatus === "error" ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Something went wrong</h3>
                <p className="text-neutral-medium">Please try again or contact me directly at ge.aivazian@gmail.com</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} name="contact" method="POST" netlify netlify-honeypot="bot-field">
                <input type="hidden" name="form-name" value="contact" />
                <p style={{ display: 'none' }}>
                  <label>
                    Don't fill this out if you're human: <input name="bot-field" />
                  </label>
                </p>
                <div className="mb-6">
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-dark mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none"
                    placeholder="John Doe"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-dark mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none"
                    placeholder="john@example.com"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-neutral-dark mb-2">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="form-input w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none"
                    placeholder="Tell me about your project or inquiry..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </form>
            )}
          </div>

          <div className="mt-12 flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
            <a href="mailto:ge.aivazian@gmail.com" className="flex items-center text-neutral-medium hover:text-primary transition-colors">
              <FiMail className="mr-2" /> ge.aivazian@gmail.com
            </a>
            <a href="https://github.com/GenaAiv" className="flex items-center text-neutral-medium hover:text-primary transition-colors">
              <FiGithub className="mr-2" /> github.com/GenaAiv
            </a>
            <a href="https://www.linkedin.com/in/gena-aivazian-813095173/" className="flex items-center text-neutral-medium hover:text-primary transition-colors">
              <FiLinkedin className="mr-2" /> linkedin.com/in/gena-aivazian
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Portfolio />} />
        <Route path="/project/:id" element={<ProjectDetail />} />
      </Routes>
    </Router>
  );
}

export default App;
